/* iter55x — Starter novel-framework templates.
 *
 * These are NOT user-published templates (those live in the community
 * marketplace and are handled by `TemplatesPicker.jsx`). These are a
 * small static catalog of well-known story structures that populate
 * a fresh project's chapters + character stubs so writers can hit the
 * ground running instead of staring at an empty Bible + blank
 * chapters.
 *
 * Applying a template:
 *   1. Updates each existing chapter (up to `template.chapters.length`)
 *      with a `title` + `status="outline"` + a starter `content` line
 *      that quotes the beat's guidance.
 *   2. Creates one `Character` per role stub via `charactersApi.create`.
 *   3. Does NOT touch variables/toggles — those stay on the API
 *      defaults set at project creation.
 *
 * Everything is data-only; the render + application logic lives in
 * `StarterTemplatesDialog.jsx` and `store.js:applyStarterTemplate`.
 */

/** @typedef {Object} StarterChapter
 *  @property {string} title
 *  @property {string} beat_note   — one-line author guidance for the beat.
 */

/** @typedef {Object} StarterCharacter
 *  @property {string} name
 *  @property {string} role
 *  @property {number} age
 *  @property {string} archetype
 *  @property {string} color         — chart-1..chart-5 (matches Radar palette)
 *  @property {string} description
 *  @property {Array<{k: string, v: number}>} traits  — 5 sliders, 0-100
 */

/** @typedef {Object} StarterTemplate
 *  @property {string} id
 *  @property {string} name
 *  @property {string} tagline
 *  @property {string} icon      — lucide-react icon name
 *  @property {StarterChapter[]}   chapters
 *  @property {StarterCharacter[]} characters
 */

const _t = (bravery, cunning, compassion, ambition, wit) => [
  { k: 'Bravery',    v: bravery    },
  { k: 'Cunning',    v: cunning    },
  { k: 'Compassion', v: compassion },
  { k: 'Ambition',   v: ambition   },
  { k: 'Wit',        v: wit        },
];

/** @type {StarterTemplate[]} */
export const STARTER_TEMPLATES = [
  {
    id: 'three_act',
    name: 'Three-Act Structure',
    tagline: 'The classical Setup → Confrontation → Resolution arc. 12 chapters.',
    icon: 'BookOpen',
    chapters: [
      { title: 'Ordinary World',        beat_note: 'Introduce the protagonist in their normal life. Hint at what is missing.' },
      { title: 'Inciting Incident',     beat_note: 'The event that upends normal. Choice or accident forces action.' },
      { title: 'Debate & Decision',     beat_note: 'Protagonist resists, then commits. Cross the first threshold.' },
      { title: 'Rising Action I',       beat_note: 'First taste of the new world. Small victories, new allies.' },
      { title: 'Rising Action II',      beat_note: 'The stakes climb. The antagonist reveals more of their hand.' },
      { title: 'Midpoint Reversal',     beat_note: 'A revelation flips the game. False victory or false defeat.' },
      { title: 'Complications',         beat_note: 'Old plans fail. Alliances fracture. Doubts return.' },
      { title: 'All Is Lost',           beat_note: 'The lowest point. What the protagonist thought they wanted seems impossible.' },
      { title: 'Dark Night of the Soul', beat_note: 'Inner reckoning. A truth learned that changes their approach.' },
      { title: 'Climax: Setup',         beat_note: 'Gather allies, resources, and resolve. Point-of-no-return.' },
      { title: 'Climax: Confrontation', beat_note: 'The final showdown. Consequences of every earlier choice arrive.' },
      { title: 'Resolution',            beat_note: 'New equilibrium. The protagonist is changed. Small quiet coda.' },
    ],
    characters: [
      { name: 'Protagonist', role: 'Protagonist', age: 28, archetype: 'Hero',
        color: 'chart-1',
        description: 'The one whose inner world we most inhabit. Wants X, needs Y, must sacrifice Z.',
        traits: _t(70, 55, 65, 60, 55) },
      { name: 'Antagonist',  role: 'Antagonist', age: 42, archetype: 'Shadow',
        color: 'chart-4',
        description: 'A dark mirror of the protagonist. Their want is coherent and terrifying.',
        traits: _t(65, 80, 25, 90, 70) },
      { name: 'Mentor',      role: 'Ally',       age: 58, archetype: 'Sage',
        color: 'chart-2',
        description: 'Guides without doing the work. Withdraws when it matters most.',
        traits: _t(55, 65, 80, 40, 85) },
    ],
  },
  {
    id: 'heros_journey',
    name: "Hero's Journey",
    tagline: 'Campbell / Vogler — 12 stages of departure, initiation, return.',
    icon: 'Compass',
    chapters: [
      { title: 'Ordinary World',       beat_note: 'Show the hero in their comfort zone. Establish what they will lose.' },
      { title: 'Call to Adventure',    beat_note: 'A herald arrives. A problem crosses the hero\u2019s threshold.' },
      { title: 'Refusal of the Call',  beat_note: 'The hero hesitates. Fear or duty holds them back.' },
      { title: 'Meeting the Mentor',   beat_note: 'A wiser figure gifts knowledge, an object, or courage.' },
      { title: 'Crossing the Threshold', beat_note: 'The special world begins. Rules are different here.' },
      { title: 'Tests, Allies, Enemies', beat_note: 'Learn who to trust. First skirmishes. A brotherhood forms.' },
      { title: 'Approach to the Inmost Cave', beat_note: 'Preparation for the central ordeal. Fear intensifies.' },
      { title: 'The Ordeal',           beat_note: 'A near-death moment (literal or symbolic). The hero is remade.' },
      { title: 'Reward (Seizing the Sword)', beat_note: 'The prize is taken. Celebration is premature.' },
      { title: 'The Road Back',        beat_note: 'The special world pursues. Return is not guaranteed.' },
      { title: 'Resurrection',         beat_note: 'The climax. The hero risks everything one final time.' },
      { title: 'Return with the Elixir', beat_note: 'Home with a gift for the ordinary world. Cycle closes.' },
    ],
    characters: [
      { name: 'The Hero',       role: 'Protagonist', age: 24, archetype: 'Hero',
        color: 'chart-1',
        description: 'Ordinary until called. Grows through trial. Their flaw is the seed of their strength.',
        traits: _t(70, 50, 70, 55, 55) },
      { name: 'The Shadow',     role: 'Antagonist',  age: 45, archetype: 'Shadow',
        color: 'chart-4',
        description: 'The hero\u2019s dark potential made flesh. Wants what the hero could have wanted.',
        traits: _t(70, 85, 20, 90, 65) },
      { name: 'The Mentor',     role: 'Ally',        age: 60, archetype: 'Sage',
        color: 'chart-2',
        description: 'Cannot walk the road for them. Dies, leaves, or falls silent at the critical moment.',
        traits: _t(50, 75, 80, 30, 90) },
      { name: 'The Herald',     role: 'Supporting',  age: 30, archetype: 'Herald',
        color: 'chart-3',
        description: 'Delivers the call. Often a stranger, sometimes a letter, occasionally a wound.',
        traits: _t(45, 60, 55, 40, 60) },
    ],
  },
  {
    id: 'save_the_cat',
    name: 'Save the Cat! (15 Beats)',
    tagline: 'Blake Snyder\u2019s beat sheet — page-perfect commercial-fiction scaffold.',
    icon: 'Sparkles',
    chapters: [
      { title: 'Opening Image',        beat_note: 'The "before" snapshot. Mirrors the Final Image.' },
      { title: 'Theme Stated',         beat_note: 'A minor character voices the story\u2019s central question.' },
      { title: 'Setup',                beat_note: 'World, cast, status quo. Six things that need fixing.' },
      { title: 'Catalyst',             beat_note: 'The messenger arrives. Life changes.' },
      { title: 'Debate',               beat_note: '"Should I go?" The protagonist wrestles.' },
      { title: 'Break Into Two',       beat_note: 'The choice is made. Act 2 begins. Rules invert.' },
      { title: 'B Story',              beat_note: 'A new relationship (usually love or friendship) begins.' },
      { title: 'Fun and Games',        beat_note: 'The "promise of the premise" delivered. Trailer moments.' },
      { title: 'Midpoint',             beat_note: 'False victory or false defeat. Stakes are re-raised.' },
      { title: 'Bad Guys Close In',    beat_note: 'The team fractures. Antagonist forces gain ground.' },
      { title: 'All Is Lost',          beat_note: 'A whiff of death. The lowest point.' },
      { title: 'Dark Night of the Soul', beat_note: 'Reflection. The hero synthesizes what they\u2019ve learned.' },
      { title: 'Break Into Three',     beat_note: 'The solution arrives. Fusion of A and B stories.' },
      { title: 'Finale',               beat_note: 'Storm the castle. Five sub-beats in sequence.' },
      { title: 'Final Image',          beat_note: 'The "after" snapshot. Change is proven.' },
    ],
    characters: [
      { name: 'Hero',           role: 'Protagonist', age: 32, archetype: 'Hero',
        color: 'chart-1',
        description: 'Save-a-cat moment early on establishes empathy. Their want is loud, their need is quiet.',
        traits: _t(70, 55, 70, 65, 60) },
      { name: 'Bad Guy',        role: 'Antagonist',  age: 46, archetype: 'Shadow',
        color: 'chart-4',
        description: 'A dark mirror. Their strategy tests every one of the hero\u2019s weaknesses.',
        traits: _t(60, 85, 25, 95, 70) },
      { name: 'B-Story Love',   role: 'Ally',        age: 30, archetype: 'Companion',
        color: 'chart-3',
        description: 'Enters at the Break Into Two. Speaks the theme. Catalyzes the internal arc.',
        traits: _t(55, 50, 80, 50, 70) },
    ],
  },
  {
    id: 'story_circle',
    name: 'Story Circle (Dan Harmon)',
    tagline: '8 beats around a circle — perfect for episodic or short novels.',
    icon: 'RotateCw',
    chapters: [
      { title: 'You (Comfort Zone)',   beat_note: 'A character in their status quo.' },
      { title: 'Need',                 beat_note: 'They want something. It has arrived at their doorstep.' },
      { title: 'Go (Unfamiliar Situation)', beat_note: 'They enter an unfamiliar situation to get it.' },
      { title: 'Search',               beat_note: 'They adapt to it. Trial and error. A new self is being built.' },
      { title: 'Find',                 beat_note: 'They get what they wanted. But it wasn\u2019t what they needed.' },
      { title: 'Take',                 beat_note: 'They pay the price. Cost is understood too late.' },
      { title: 'Return',               beat_note: 'They come back to the familiar. It looks different now.' },
      { title: 'Change',               beat_note: 'They (and the world) are transformed. Comfort zone is new.' },
    ],
    characters: [
      { name: 'The One',        role: 'Protagonist', age: 26, archetype: 'Hero',
        color: 'chart-1',
        description: 'Every beat is filtered through their subjective experience.',
        traits: _t(60, 50, 65, 55, 55) },
      { name: 'The Other',      role: 'Foil',        age: 26, archetype: 'Trickster',
        color: 'chart-3',
        description: 'A parallel journey. Mirror or contrast, but always contrast in the end.',
        traits: _t(65, 75, 40, 70, 80) },
    ],
  },
  {
    id: 'dark_romance_sequel',
    name: 'Dark Romance — Sequel',
    tagline: 'Book 2 in the same dark romance world. Carries the same couple forward — new external threat, new stakes.',
    icon: 'Skull',
    variables: [
      { key: 'hero_name', label: 'Hero name (from Book 1)', type: 'text', default: 'Roman Volkov', placeholder: 'e.g. Roman Volkov',
        random: ['Roman Volkov', 'Luca Corvinus', 'Kieran Vasquez', 'Zayn Kaslov', 'Dante Marchetti'] },
      { key: 'heroine_name', label: 'Heroine name (from Book 1)', type: 'text', default: 'Isla Moreau', placeholder: 'e.g. Isla Moreau',
        random: ['Isla Moreau', 'Ada Winters', 'Odessa Rhee', 'Clover Kenna'] },
      { key: 'couple_status', label: 'Where the couple is at the start of Book 2', type: 'radio',
        options: ['Newly married', 'Living together, unofficially', 'Ruling the empire side-by-side', 'Reconciled after a public scandal'],
        default: 'Newly married' },
      { key: 'time_gap', label: 'Time since Book 1', type: 'radio',
        options: ['6 months later', '1 year later', '2 years later', '5 years later'],
        default: '1 year later' },
      { key: 'prior_secret', label: 'The secret that nearly broke them in Book 1', type: 'text',
        default: 'he had ordered the hit that killed her father',
        placeholder: 'carry Book 1\u2019s Act 4 betrayal forward',
        random: [
          'he had ordered the hit that killed her father',
          'he was hired by her enemies to kidnap her',
          'he ran the empire her family tried to bring down',
        ] },
      { key: 'new_threat', label: 'The new external threat in Book 2', type: 'radio',
        options: ['A rival syndicate rising in his territory', 'A federal investigation into her family',
                  'His long-lost sibling returning for revenge', 'A blackmailer with proof of Book 1\u2019s crimes'],
        default: 'A rival syndicate rising in his territory' },
      { key: 'new_stakes', label: 'What they stand to lose', type: 'radio', multi: true,
        options: ['Their empire', 'Their child', 'Their freedom', 'Each other'],
        default: ['Their empire', 'Each other'] },
      { key: 'growth_test', label: 'The test that proves how far she\u2019s come', type: 'text',
        default: 'she must lead a meeting of his rivals alone, in his place',
        placeholder: 'a moment she couldn\u2019t have handled in Book 1',
        random: [
          'she must lead a meeting of his rivals alone, in his place',
          'she must interrogate a traitor without breaking',
          'she must broker peace with the family that hunted her',
          'she must pull the trigger to save him',
        ] },
      { key: 'setting', label: 'Setting', type: 'radio',
        options: ['Same city as Book 1', 'A new territory they\u2019re expanding into', 'Isolated country estate', 'International — abroad'],
        default: 'Same city as Book 1' },
      { key: 'pov', label: 'POV', type: 'radio',
        options: ['Alternating dual first-person', 'First-person Heroine only', 'First-person Hero only'],
        default: 'Alternating dual first-person' },
    ],
    chapters: [
      { title: 'Act 1 — The New Normal Cracks',
        beat_note:
          'Approx. 8,000 words. POV: {{pov}}. Setting: {{setting}}. {{time_gap}} since Book 1.\n' +
          '1) Show the couple\u2019s twisted "normal" \u2014 {{hero_name}} & {{heroine_name}} are {{couple_status}}. Callbacks to Book 1\u2019s scars.\n' +
          '2) Introduce {{new_threat}} \u2014 subtly at first, one small sign either could dismiss.\n' +
          '3) A quiet intimacy scene that shows how far the trust has come since Book 1\u2019s betrayal ({{prior_secret}}).\n' +
          'Ending Hook \u2014 the new threat lands publicly. What they stand to lose: {{new_stakes}}.' },
      { title: 'Act 2 — Old Wounds Reopen',
        beat_note:
          'Approx. 10,000 words. Continue from Act 1.\n' +
          '1) {{heroine_name}} takes the lead \u2014 first move of {{growth_test}}. Show her strength.\n' +
          '2) {{hero_name}}\u2019s old instinct is to hide her away. Old fight, new register \u2014 she pushes back and wins the argument.\n' +
          '3) A scene where a Book 1 side character (rival, family, mentor) returns with a warning that echoes {{prior_secret}}.\n' +
          '4) A love scene that recontextualises Book 1\u2019s Act 2 dub-con \u2014 same act, opposite emotional charge; she initiates.\n' +
          'Ending Hook \u2014 the new threat proves it knows something only Book 1\u2019s inner circle could know.' },
      { title: 'Act 3 — The Partnership Under Fire',
        beat_note:
          'Approx. 10,000 words. Continue from Act 2.\n' +
          '1) They plan the counter-move together. Split perspectives; sync on the outcome.\n' +
          '2) {{growth_test}} lands \u2014 she executes it. He watches, terrified and proud.\n' +
          '3) Setback \u2014 someone in their circle betrays them. Book 1\u2019s Act 4 pattern rhymes but doesn\u2019t repeat.\n' +
          '4) Emotional beat: they name the fear that they might lose each other again. This time neither runs.\n' +
          'Ending Hook \u2014 the traitor\u2019s identity is revealed, and it costs them one of {{new_stakes}}.' },
      { title: 'Act 4 — The Choice',
        beat_note:
          'Approx. 8,000 words. Continue from Act 3.\n' +
          '1) Aftermath of the loss. Grief written in each character\u2019s voice.\n' +
          '2) The choice \u2014 fight the threat their old way (bloodshed) or the new way (leverage + coalition). {{hero_name}} wants old-way; {{heroine_name}} pitches new-way.\n' +
          '3) They compromise \u2014 the new-way plan uses the empire\u2019s ruthlessness without becoming Book 1\u2019s monster again.\n' +
          '4) A tender scene that pays off Book 1\u2019s Act 5 grand gesture in reverse: this time SHE makes the sacrifice.\n' +
          'Ending Hook \u2014 the final confrontation is set. Meet at dawn.' },
      { title: 'Act 5 — The Reign (+ Epilogue)',
        beat_note:
          'Approx. 6,000 words. Continue from Act 4.\n' +
          '1) Final confrontation \u2014 they end {{new_threat}} together. She fires the shot / signs the papers / calls the vote that ends it.\n' +
          '2) The empire changes hands or reshapes. Book 1\u2019s crown becomes a shared throne.\n' +
          '3) Quiet aftermath \u2014 a Book 1 callback in a Book 2 emotional key.\n' +
          'Epilogue \u2014 A year later. New life. A moment that seeds Book 3 (a letter arrives, a child\u2019s question, a rival waking up).\n' +
          'Final line \u2014 She is not his queen anymore. They are the throne.' },
    ],
    characters: [
      { name: '{{hero_name}}', role: 'Protagonist', age: 34, archetype: 'Reformed Captor',
        color: 'chart-4',
        description:
          'Same {{hero_name}} from Book 1, {{time_gap}} on. Empire intact, but softened at the edges by {{heroine_name}}. ' +
          'Old instincts (control, isolation, violence) still fire under pressure \u2014 he\u2019s learning to route them through her.',
        traits: _t(75, 90, 40, 90, 75) },
      { name: '{{heroine_name}}', role: 'Protagonist', age: 25, archetype: 'Partner-Queen',
        color: 'chart-1',
        description:
          'Same {{heroine_name}} from Book 1, {{time_gap}} on. No longer a captive \u2014 co-ruler. ' +
          'The Book 2 test: {{growth_test}}. Book 1\u2019s vulnerability has become Book 2\u2019s strategic patience.',
        traits: _t(90, 75, 70, 90, 85) },
    ],
  },
  {
    id: 'dark_romance',
    name: 'Dark Romance',
    tagline: 'Morally-gray captor x reluctant heroine — 5 acts, ~48k words. Mafia / Stalker / Captor variants.',
    icon: 'Skull',
    variables: [
      { key: 'hero_name', label: 'Hero name', type: 'text', default: 'Roman Volkov', placeholder: 'e.g. Roman Volkov',
        random: ['Roman Volkov', 'Luca Corvinus', 'Kieran Vasquez', 'Zayn Kaslov', 'Dante Marchetti', 'Ivan Blackthorn'] },
      { key: 'hero_age', label: 'Hero age', type: 'text', default: '32', placeholder: '28–42',
        random: ['28', '30', '32', '34', '36', '38', '40'] },
      { key: 'hero_profession', label: 'Hero identity', type: 'radio', multi: true,
        options: ['Mafia boss', 'Stalker', 'Captor', 'Bully / Rival heir'], default: ['Mafia boss'] },
      { key: 'hero_secret', label: 'Hero dark secret (one line)', type: 'text',
        default: 'ordered the hit that killed her father',
        placeholder: 'what would destroy her trust',
        random: [
          'ordered the hit that killed her father',
          'was hired by her enemies to kidnap her',
          'has watched her for years before she ever saw him',
          'runs the empire her family tried to bring down',
          'made a blood oath to end her bloodline',
        ] },
      { key: 'hero_vulnerable', label: 'Hero vulnerable trait (one line)', type: 'text',
        default: 'a childhood scar he never lets anyone touch',
        placeholder: 'the crack in the armor',
        random: [
          'a childhood scar he never lets anyone touch',
          'a stray dog he secretly feeds every night',
          'his younger sister\u2019s name tattooed over his heart',
          'nightmares that only her voice can end',
          'a rosary hidden in his desk drawer',
        ] },
      { key: 'heroine_name', label: 'Heroine name', type: 'text', default: 'Isla Moreau', placeholder: 'e.g. Isla Moreau',
        random: ['Isla Moreau', 'Ada Winters', 'Odessa Rhee', 'Clover Kenna', 'Sasha Delaney', 'Wren Bishop'] },
      { key: 'heroine_age', label: 'Heroine age', type: 'text', default: '23', placeholder: '20–28',
        random: ['21', '22', '23', '24', '25', '26'] },
      { key: 'heroine_backstory', label: 'Heroine tragic backstory (one line)', type: 'text',
        default: 'her father was murdered in front of her at fifteen',
        placeholder: 'the wound she carries',
        random: [
          'her father was murdered in front of her at fifteen',
          'she grew up in foster care after the fire',
          'her mother disappeared with the family fortune',
          'she survived the accident that killed her twin',
          'her sister is missing and everyone stopped looking',
        ] },
      { key: 'heroine_goal', label: 'Heroine goal she\u2019s fighting for', type: 'radio',
        options: ['Find her missing sister', 'Save her family estate', 'Finish her degree', 'Avenge her father', 'Escape her past'],
        default: 'Find her missing sister' },
      { key: 'setting', label: 'Setting', type: 'radio',
        options: ['Modern City underworld', 'Dark Academia campus', 'Coastal mafia territory', 'Isolated country estate'],
        default: 'Modern City underworld' },
      { key: 'trap', label: 'What traps her in his world', type: 'radio',
        options: ['Kidnapping', 'Forced marriage contract', 'Blackmail / debt', 'Witness protection under his roof'],
        default: 'Kidnapping' },
      { key: 'pov', label: 'POV', type: 'radio',
        options: ['First-person Heroine (with occasional Hero chapters)', 'First-person Heroine only', 'Alternating dual first-person'],
        default: 'First-person Heroine (with occasional Hero chapters)' },
    ],
    chapters: [
      { title: 'Act 1 — The Meet-Danger',
        beat_note:
          'Approx. 8,000\u201310,000 words. POV: {{pov}}. Setting: {{setting}}. Genre lean: {{hero_profession}}.\n' +
          '1) Hook (in media res) \u2014 {{heroine_name}} ({{heroine_age}}) in a vulnerable, desperate, or dangerous situation tied to her backstory: {{heroine_backstory}}. She fights for one thing: {{heroine_goal}}.\n' +
          '2) First Encounter \u2014 {{hero_name}} ({{hero_age}}, {{hero_profession}}) arrives NOT as a savior but as a THREAT: powerful, cold, terrifying. He may rescue her, but only to claim her.\n' +
          '3) The Trap \u2014 By end of Act 1 she is trapped via {{trap}}. She fears him. Flicker of dark curiosity.\n' +
          'Key Scene \u2014 "The offer she can\u2019t refuse." She says no. He shows her she has no choice.\n' +
          'Ending Hook \u2014 She is in his house / territory. The door locks behind her. Escape is impossible.' },
      { title: 'Act 2 — Resistance & Confusion',
        beat_note:
          'Approx. 12,000\u201315,000 words. Continue from Act 1: she is trapped via {{trap}}.\n' +
          '1) The Push \u2014 she actively fights: escape attempts, refusing food, verbal attacks. Show her strength.\n' +
          '2) Cracks in the Armor \u2014 she witnesses unexpected humanity ({{hero_vulnerable}}). She questions if he\u2019s pure evil.\n' +
          '3) First Forced Intimacy \u2014 coercive/dub-con encounter. Body betrays her. Shame + confusion; hate and desire blur.\n' +
          '4) She Learns a Secret \u2014 backstory that explains his cruelty. She doesn\u2019t forgive; she understands.\n' +
          'Ending Hook \u2014 he protects her from an external threat (rival, gunman). She sees his possessive obsession as a twisted form of protection.' },
      { title: 'Act 3 — The Shift',
        beat_note:
          'Approx. 10,000\u201312,000 words. Continue from Act 2: she has seen his vulnerability.\n' +
          '1) Turning Point \u2014 she stops running. Actively chooses to stay \u2014 out of dark curiosity or a growing bond. Sees him as a person, not a monster.\n' +
          '2) Mutual Vulnerability \u2014 she nurses him after an injury OR he admits a deep fear/secret. He shows jealousy \u2014 not just possessive but *hurt*. First emotional exposure.\n' +
          '3) Power Shift \u2014 balance equalises. She pushes back; he lets her. He respects her fire.\n' +
          '4) Emotional Climax (first "I see you") \u2014 she admits she feels something \u2014 not love, but a twisted connection. He admits she\u2019s not just possession, but "his."\n' +
          'Ending Hook \u2014 third party reveals a devastating truth: {{hero_secret}}. Her world shatters. She feels used.' },
      { title: 'Act 4 — The Darkest Moment',
        beat_note:
          'Approx. 8,000\u201310,000 words. Continue from Act 3: she has discovered the betrayal ({{hero_secret}}).\n' +
          '1) Confrontation \u2014 she confronts him. He doesn\u2019t deny it. Admits ulterior motives but insists he *changed*. She doesn\u2019t believe him.\n' +
          '2) Separation \u2014 she leaves (or he pushes her away to "protect" her). Both devastated. She realises she wants him even knowing the worst.\n' +
          '3) The Spiral \u2014 both spiral. He becomes reckless, self-destructive, violent. Her "safe" world feels hollow.\n' +
          '4) The Desperate Act \u2014 he does something extreme to prove loyalty (kills his mentor, abandons his empire, breaks his blood oath). She hears about it.\n' +
          'Ending Hook \u2014 she chooses him. Not as victim, as willing partner. She knocks on his door \u2014 or finds him in a dangerous place.' },
      { title: 'Act 5 — The Earned HEA (+ Epilogue)',
        beat_note:
          'Approx. 6,000\u20138,000 words (Act 5 + Epilogue). Continue from Act 4: she has returned to him willingly.\n' +
          '1) Reunion \u2014 she finds him broken/in danger. She saves him (physically or emotionally). Power dynamic flips \u2014 she is his savior.\n' +
          '2) Final Threat \u2014 external enemy (rival, police, vengeful family) attacks. They fight together as a united front. She proves she\u2019s a partner in his darkness.\n' +
          '3) Grand Gesture \u2014 he proves his obsession is now love: gives up his empire, or kills the final threat and says, "It was always for you."\n' +
          '4) Earned HEA \u2014 together, but not white-picket. Equals in a dark world. She accepts him fully; he accepts her strength.\n' +
          'Epilogue (1\u20132 years later) \u2014 their twisted "normal" in {{setting}}: quiet moment at home, a pregnancy, him teaching her to shoot, or a rival family taken down together. End on a line that echoes the opening hook.\n' +
          'Final line \u2014 "She is no longer his captive. She is his queen."' },
    ],
    characters: [
      { name: '{{hero_name}}', role: 'Protagonist', age: 32, archetype: 'Morally-Gray Captor',
        color: 'chart-4',
        description:
          '{{hero_age}}-year-old {{hero_profession}} operating out of {{setting}}. Dominant, possessive, dangerous. ' +
          'Dark secret: {{hero_secret}}. Vulnerable trait: {{hero_vulnerable}}. ' +
          'Traps the heroine via {{trap}}. Obsession first, love later \u2014 must EARN the HEA.',
        traits: _t(75, 90, 15, 95, 75) },
      { name: '{{heroine_name}}', role: 'Protagonist', age: 23, archetype: 'Reluctant Fighter',
        color: 'chart-1',
        description:
          '{{heroine_age}}-year-old woman shaped by tragedy: {{heroine_backstory}}. ' +
          'Strong-willed, vulnerable but resilient. Fighting for one clear goal: {{heroine_goal}}. ' +
          'Trapped in his world via {{trap}} \u2014 refuses to be a victim; forces him to earn her, willingly.',
        traits: _t(85, 55, 70, 80, 70) },
    ],
  },
  {
    id: 'dark_contemporary_romance_blend',
    name: 'Dark Contemporary Romance (Blended)',
    tagline: 'Morally-gray tycoon x ambitious heroine — 5 acts, ~44k words. Mix-and-match variables.',
    icon: 'Flame',
    // Mix-and-match variables. TemplateVariablesDialog renders a prompt
    // for every entry here before the chapters/characters are applied.
    // Values are interpolated into `beat_note` + character `description`
    // via `{{token}}` tags. `default` seeds the initial state.
    variables: [
      { key: 'hero_name',       label: 'Hero name',      type: 'text',   default: 'Damian Wolfe',   placeholder: 'e.g. Damian Wolfe',
        random: ['Damian Wolfe', 'Kade Ashcroft', 'Nikolai Voss', 'Cassian Frost', 'Rhys Blackwood', 'Adrian Kane', 'Silas Marchetti'] },
      { key: 'hero_age',        label: 'Hero age',       type: 'text',   default: '36',             placeholder: '30–45',
        random: ['32', '34', '36', '38', '40', '42'] },
      { key: 'hero_profession', label: 'Hero profession', type: 'radio',  multi: true,
        options: ['CEO', 'Lawyer', 'Landlord', 'Professor'],  default: ['CEO'] },
      { key: 'hero_trauma',     label: 'Hero traumatic past (one line)',      type: 'text',
        default: 'lost his younger sibling in a preventable accident',
        placeholder: 'what made him controlling',
        random: [
          'lost his younger sibling in a preventable accident',
          'watched his family empire crumble under his father\u2019s ruin',
          'was betrayed by his first love who married his rival',
          'survived a hostile takeover that ended a mentor\u2019s life',
          'grew up unwanted — adopted only for tax purposes',
        ] },
      { key: 'hero_vulnerability', label: 'Hero secret vulnerability (one line)', type: 'text',
        default: 'silent panic attacks he hides from everyone',
        placeholder: 'the crack in the armor',
        random: [
          'silent panic attacks he hides from everyone',
          'insomnia that only breaks when she sleeps beside him',
          'a hidden charity funding orphans in his brother\u2019s name',
          'his mother\u2019s dementia — visited nightly, never spoken of',
          'a scar shaped like the day he almost died',
        ] },
      { key: 'heroine_name',    label: 'Heroine name',   type: 'text',   default: 'Nora Vance',     placeholder: 'e.g. Nora Vance',
        random: ['Nora Vance', 'Elena Cross', 'Sadie Reyes', 'Iris Kowalski', 'Millie Chen', 'Juno Alvarez', 'Frankie Doyle'] },
      { key: 'heroine_age',     label: 'Heroine age',    type: 'text',   default: '25',             placeholder: '22–30',
        random: ['22', '23', '24', '25', '26', '28'] },
      { key: 'heroine_career',  label: 'Heroine career goal', type: 'radio', multi: true,
        options: ['Architect', 'Journalist', 'Student', 'Waitress'], default: ['Architect'] },
      { key: 'heroine_pressure', label: 'Heroine pressing problem', type: 'radio',
        options: ['Unpaid tuition', 'Firm takeover', 'Eviction', 'Inherited family debt'],
        default: 'Firm takeover' },
      { key: 'city',            label: 'City / setting',  type: 'text',
        default: 'New York',
        placeholder: 'New York, London, Singapore…',
        random: ['New York', 'London', 'Singapore', 'Chicago', 'Hong Kong', 'Dubai', 'Toronto'] },
      { key: 'leverage',        label: 'Power he holds over her', type: 'radio',
        options: ['Her career', 'Her housing', 'Her family debt'], default: 'Her career' },
      { key: 'pov',             label: 'POV', type: 'radio',
        options: ['First-person Heroine', 'First-person Hero', 'Alternating dual first-person'],
        default: 'First-person Heroine' },
    ],
    chapters: [
      { title: 'Act 1 — The Power Meet',
        beat_note:
          'Approx. 8,000 words. POV: {{pov}}. Setting: {{city}}.\n' +
          '1) Hook — Show {{heroine_name}} ({{heroine_age}}, aspiring {{heroine_career}}) crushed by {{heroine_pressure}}; desperate but proud.\n' +
          '2) First Encounter — She meets {{hero_name}} ({{hero_age}}, {{hero_profession}}) in a professional context. He is cold, arrogant, dismisses her. She hates him.\n' +
          '3) The Trap — He offers a solution tied to {{leverage}}, but the terms are a binding, humiliating contract. She has 24 hours.\n' +
          '4) Hook Ending — She signs. He watches with a possessive glint: "You\u2019re mine now. Legally and professionally. Don\u2019t forget it."' },
      { title: 'Act 2 — Resistance & Psychological Control',
        beat_note:
          'Approx. 12,000 words. Continue from Act 1: she is bound by the contract.\n' +
          '1) The Game Begins — She hunts loopholes. He counters ruthlessly and enjoys the battle.\n' +
          '2) Forced Proximity — Meetings, galas, family dinners in {{city}}. Tension unbearable.\n' +
          '3) First Dark Twist — He manipulates her life outside the contract (her boss, her building, her alternatives). She realizes he wants her isolated and dependent — financially and socially.\n' +
          '4) First Intimate Scene (dub-con via power imbalance) — Framed as a power game, not physical force. She hates that her body responds.\n' +
          '5) Ending Hook — She glimpses his vulnerability: {{hero_vulnerability}}. She begins to see him as damaged, not just evil.' },
      { title: 'Act 3 — The Shift',
        beat_note:
          'Approx. 10,000 words. Continue from Act 2: she has seen his vulnerability.\n' +
          '1) She Stops Fighting — Starts studying him. Asks real questions. Guarded answers. Twisted intimacy builds.\n' +
          '2) Possessive Protection — Someone at work harasses her; {{hero_name}} destroys that person\u2019s career quietly and completely. She is equal parts terrified and grateful.\n' +
          '3) Equalizing Power — She calls him out: "You don\u2019t own me. You own the contract. There\u2019s a difference." He looks unsettled for the first time. He wants her to stay willingly.\n' +
          '4) Emotional Climax — Real conversation, no contract, no power plays. He admits he\u2019s terrified of being vulnerable ({{hero_trauma}}). She admits she\u2019s terrified of needing him.\n' +
          '5) Ending Hook — An external threat emerges (rival, family, press). He must choose between his empire and protecting her.' },
      { title: 'Act 4 — The Darkest Moment',
        beat_note:
          'Approx. 8,000 words. Continue from Act 3: an external threat closing in.\n' +
          '1) The Betrayal — She discovers the full extent of his manipulation: he orchestrated her original {{heroine_pressure}} to make her dependent on him. She confronts him.\n' +
          '2) The Break — She tears up the contract and walks out: "I\u2019d rather be broke and free than rich and yours."\n' +
          '3) The Spiral — He is too proud to chase, but destroys his own empire in the background to neutralize the threat anyway. She hears about it.\n' +
          '4) Her Realization — Back in the "safe" world she feels empty. She misses the intensity, not the money. She loves him — despite everything.\n' +
          '5) Ending Hook — She shows up at his penthouse. He\u2019s drunk, broken, alone. She kneels beside him: "I\u2019m not here for the contract. I\u2019m here for you."' },
      { title: 'Act 5 — The Earned, Real-World HEA',
        beat_note:
          'Approx. 6,000 words (Act 5 + Epilogue). Continue from Act 4: she has returned willingly.\n' +
          '1) The Rebuild — No contract, no coercion. He hands her all passwords, accounts, legal rights: "I\u2019m giving you the power now."\n' +
          '2) Final Threat — The rival attacks. They fight it together, legally and publicly. She speaks at the press conference, showing she is his equal.\n' +
          '3) Grand Gesture — He proves he values her over power (steps down as {{hero_profession}}, follows her for her dream {{heroine_career}} job, etc.). Power shift complete.\n' +
          '4) Earned HEA — Partners in love, business, life. She is not a victim; she is his Queen. He is not a villain; he is her King.\n' +
          'Epilogue (1 year later) — Show their balanced life in {{city}}. He still has edges (jealous, possessive), but she now calls the shots. Close on a line that shows she chose this darkness.' },
    ],
    characters: [
      { name: '{{hero_name}}', role: 'Protagonist', age: 36, archetype: 'Morally-Gray Tycoon',
        color: 'chart-4',
        description:
          '{{hero_age}}-year-old {{hero_profession}} in {{city}}. Ultra-wealthy, possessive, controlling. ' +
          'Traumatic past: {{hero_trauma}}. Secret vulnerability: {{hero_vulnerability}}. ' +
          'He holds leverage over the heroine via {{leverage}}. Wants her isolated and dependent — not physically forced.',
        traits: _t(60, 90, 20, 95, 80) },
      { name: '{{heroine_name}}', role: 'Protagonist', age: 25, archetype: 'Ambitious Everywoman',
        color: 'chart-1',
        description:
          '{{heroine_age}}-year-old aspiring {{heroine_career}} in {{city}}. Relatable, middle-class, strong-willed. ' +
          'Pressing crisis: {{heroine_pressure}}. Signs his contract to survive. Refuses to be a puppet — eventually forces him to earn her, willingly.',
        traits: _t(80, 60, 75, 85, 70) },
    ],
  },
];

export function findStarterTemplate(id) {
  return STARTER_TEMPLATES.find((t) => t.id === id) || null;
}
